import { CATEGORIES } from '@sse/schema';
import {
  CalendarClock,
  Clapperboard,
  ExternalLink,
  Image as ImageIcon,
  Loader2,
  Pencil,
  Plus,
  Search,
  Send,
  SkipForward,
  Smartphone,
  StickyNote,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { Card, Field, btnGhost, btnPrimary, btnSecondary, inputCls } from '../components/ui';
import { ApiError, type AdminEventRow, type Api } from '../lib/api';
import { categoryLabel, formatRange, formatStamp } from '../lib/format';
import { useSession } from '../lib/session';
import { postWithProgress } from '../lib/upload';

type Mode = 'off' | 'review' | 'auto';
type Kind = 'post' | 'story' | 'reel';
interface Config {
  mode: Mode;
  reviewMinutes: number;
  leadMinutes: number;
  activeFrom: string | null;
}
interface Params {
  variant?: 'auto' | 'lista' | 'mappa';
  days?: number;
  cards?: number;
  offset?: number;
  category?: string;
  output?: 'post' | 'story';
  eventId?: string;
  featured?: boolean;
  occasion?: string;
}
interface Gen {
  label: string;
  kind: Kind;
  what: string;
  when: string;
  params: readonly string[];
  customOnly?: boolean;
  /** reel-speciale: le occasioni della rubrica «Giorni speciali» */
  occasions?: Record<string, string>;
}
interface Schedule {
  id: string;
  name: string;
  generator: string;
  params: Params;
  days: number[];
  monthDay?: number | null;
  time: string;
  enabled: boolean;
  from?: string | null;
  until?: string | null;
  note?: string | null;
}
interface Post {
  id: string;
  format: string;
  kind?: Kind;
  title?: string;
  status: string;
  createdAt: string;
  publishAt?: string;
  publishedAt?: string;
  caption: string;
  images: string[];
  video?: string;
  manual?: boolean;
  results?: Record<string, { id: string; permalink?: string }>;
  errors?: string[];
  decidedBy?: string;
}
interface MediaItem {
  path: string;
  type?: 'image' | 'video';
}
interface Entry {
  key: string;
  date: string;
  time: string;
  source: 'schedule' | 'slot';
  scheduleId?: string;
  slotId?: string;
  title: string;
  generator?: string;
  params?: Params;
  kind: Kind;
  when?: string;
  off: boolean;
  note?: string;
  manual?: boolean;
  sticker?: string;
  caption?: string;
  media?: MediaItem[];
  group?: string;
  slotStatus?: string;
  posts: Post[];
}
interface Data {
  config: Config;
  generators: Record<string, Gen>;
  schedules: Schedule[];
  programme: Entry[];
  posts: Post[];
  today: string;
  connected: boolean;
}
interface SlotForm {
  id?: string;
  date: string;
  time: string;
  title: string;
  kind: Kind;
  source: 'template' | 'ready';
  generator: string;
  params: Params;
  media: MediaItem[];
  caption: string;
  manual: boolean;
  sticker: string;
  note: string;
  off: boolean;
  group: string;
}

const MODES: Record<Mode, string> = {
  off: 'Spenta: nessun post',
  review: 'Anteprima: su Telegram, si pubblica con la conferma',
  auto: 'Automatica: si pubblica all’ora prevista',
};
const KINDS: Record<Kind, { label: string; icon: typeof ImageIcon }> = {
  post: { label: 'Post', icon: ImageIcon },
  story: { label: 'Storia', icon: Smartphone },
  reel: { label: 'Reel', icon: Clapperboard },
};
const STATUS: Record<string, [string, string]> = {
  rendering: ['Video in preparazione', 'bg-surface-strong'],
  review: ['In anteprima su Telegram', 'bg-accent-soft'],
  approved: ['Confermato', 'bg-accent-soft'],
  publishing: ['In pubblicazione', 'bg-accent-soft'],
  published: ['Pubblicato', 'bg-ok-soft'],
  manual: ['Mandato su Telegram, da caricare a mano', 'bg-ok-soft'],
  failed: ['Non riuscito', 'bg-danger-soft'],
  skipped: ['Saltato', 'bg-surface-strong'],
  expired: ['Scaduto', 'bg-surface-strong'],
};
const SLOT_STATUS: Record<string, [string, string]> = {
  missed: ['Non preparato (ora passata)', 'bg-danger-soft'],
  empty: ['Niente da pubblicare', 'bg-surface-strong'],
};
const NETWORKS: Record<string, string> = {
  instagram: 'Instagram',
  reel: 'Reel',
  story: 'Storia Instagram',
  facebook: 'Facebook',
  facebook_story: 'Storia Facebook',
};
const WEEK = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const WEEKDAYS = ['lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato', 'domenica'];
const OFFSETS = [
  'lo stesso giorno',
  'il giorno dopo',
  'tra 2 giorni',
  'tra 3 giorni',
  'tra 4 giorni',
  'tra 5 giorni',
  'tra 6 giorni',
];
const UPLOAD_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
};
const dayFmt = new Intl.DateTimeFormat('it-IT', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  timeZone: 'UTC',
});
const dayLabel = (day: string) => {
  const s = dayFmt.format(new Date(`${day}T12:00:00Z`));
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const hhmm = (iso: string) =>
  new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
const nowRome = () =>
  new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
    .format(new Date())
    .replace(' ', 'T');
const isVideo = (m: MediaItem) => m.type === 'video' || /\.(mp4|mov)$/i.test(m.path);
const errorText = (e: unknown) =>
  e instanceof ApiError
    ? ((e.body as { message?: string; error?: string }).error ?? e.message)
    : e instanceof Error
      ? e.message
      : String(e);

function daysText(s: Schedule) {
  if (s.monthDay && s.days.length)
    return `il primo ${s.days.map((d) => WEEKDAYS[d]).join(' o ')} dal ${s.monthDay} del mese`;
  if (s.monthDay) return `il ${s.monthDay} di ogni mese`;
  if (s.days.length === 7) return 'ogni giorno';
  return s.days.map((d) => WEEK[d]).join(', ');
}

/**
 * Post e storie social organici (docs/07-admin-e-bot.md, "Social"). Le **schedulazioni**
 * ripetono un generatore (oggi, weekend, l’evento del giorno…) in certi giorni e creano la
 * **programmazione**; nella programmazione si spegne una data, si aggiunge una nota o si inserisce
 * un contenuto personalizzato (da generare o con i file già pronti). La Lambda `social` prepara
 * ogni post poco prima dell’ora, lo manda su Telegram e lo pubblica all’ora prevista se
 * confermato. `?event=<id>` apre un nuovo contenuto con la scheda di quell’evento.
 */
export function Social() {
  const { api, isAdmin, canEdit } = useSession();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<'programme' | 'schedules' | 'history' | 'settings'>('programme');
  const [days, setDays] = useState(21);
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [slotForm, setSlotForm] = useState<SlotForm | null>(null);

  const load = async () => {
    try {
      setData(await api<Data>(`/social?days=${days}`));
    } catch (e) {
      setError(errorText(e));
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  // dalla scheda di un evento: nuovo contenuto con quell'evento
  useEffect(() => {
    const event = params.get('event');
    if (!event) return;
    setTab('programme');
    setSlotForm({
      ...emptySlot(),
      title: 'Scheda evento',
      source: 'template',
      generator: 'evento',
      params: { eventId: event, output: 'post' },
    });
    setParams({}, { replace: true });
  }, [params, setParams]);

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (ok) setNotice(ok);
      await load();
      return true;
    } catch (e) {
      setError(errorText(e));
      return false;
    }
  };

  const tabs: [typeof tab, string][] = [
    ['programme', 'Programmazione'],
    ['schedules', 'Schedulazioni'],
    ['history', 'Pubblicati'],
    ['settings', 'Impostazioni'],
  ];

  return (
    <main className="mx-auto max-w-[1200px] p-4 md:p-6">
      <h1 className="font-display text-h2">Social</h1>
      <p className="mt-1 max-w-[75ch] text-sm text-muted">
        Post e storie su Instagram e Facebook (solo organici). Le schedulazioni creano la
        programmazione; ogni contenuto si prepara poco prima dell’ora, arriva su Telegram e si
        pubblica all’ora prevista se lo confermi. Le storie con sticker (sondaggio, domanda, link)
        sono «a mano»: all’ora prevista ti arrivano i file da caricare.
      </p>
      <nav className="mt-4 flex gap-1 border-b border-surface-strong" aria-label="Sezioni">
        {tabs.map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            aria-current={tab === k ? 'page' : undefined}
            className={`px-3 py-2 text-label font-medium uppercase tracking-[0.04em] ${tab === k ? 'border-b-2 border-fg' : 'text-muted hover:text-fg'}`}
          >
            {label}
          </button>
        ))}
      </nav>
      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      {notice && (
        <p role="status" className="mt-4 bg-ok-soft p-3 text-sm">
          {notice}
        </p>
      )}
      {!data ? (
        <p className="mt-4 text-sm text-muted">Caricamento…</p>
      ) : (
        <div className="mt-4 flex flex-col gap-6">
          {data.config.mode === 'off' && (
            <p className="bg-danger-soft p-3 text-sm">
              La pubblicazione è spenta: la programmazione non prepara niente (Impostazioni).
            </p>
          )}
          {data.config.activeFrom && data.config.activeFrom > data.today && (
            <p className="bg-accent-soft p-3 text-sm">
              Le schedulazioni partono dal {dayLabel(data.config.activeFrom).toLowerCase()}, quando
              va online la nuova piattaforma: fino ad allora escono solo i contenuti personalizzati
              (data in Impostazioni).
            </p>
          )}
          {tab === 'programme' && (
            <>
              {slotForm && (
                <SlotEditor
                  api={api}
                  generators={data.generators}
                  initial={slotForm}
                  onCancel={() => setSlotForm(null)}
                  onSave={async (body, id) => {
                    const ok = await run(
                      () =>
                        api(id ? `/social/slots/${id}` : '/social/slots', {
                          method: id ? 'PUT' : 'POST',
                          body,
                        }),
                      'Contenuto salvato nella programmazione.',
                    );
                    if (ok) setSlotForm(null);
                  }}
                />
              )}
              <Programme
                data={data}
                days={days}
                setDays={setDays}
                canEdit={canEdit}
                onAdd={() => setSlotForm(emptySlot())}
                onEdit={(e) => setSlotForm(entryToForm(e))}
                run={run}
                api={api}
              />
            </>
          )}
          {tab === 'schedules' && <Schedules data={data} canEdit={canEdit} run={run} api={api} />}
          {tab === 'history' && (
            <History posts={data.posts} run={run} api={api} canEdit={canEdit} />
          )}
          {tab === 'settings' && (
            <Settings config={data.config} isAdmin={isAdmin} run={run} api={api} />
          )}
        </div>
      )}
    </main>
  );
}

function emptySlot(): SlotForm {
  const [date, time] = nowRome().split('T') as [string, string];
  const next = `${String((Number(time.slice(0, 2)) + 2) % 24).padStart(2, '0')}:00`;
  return {
    date,
    time: next,
    title: '',
    kind: 'post',
    source: 'ready',
    generator: 'evento',
    params: {},
    media: [],
    caption: '',
    manual: false,
    sticker: '',
    note: '',
    off: false,
    group: '',
  };
}

function entryToForm(e: Entry): SlotForm {
  return {
    id: e.slotId!,
    date: e.date,
    time: e.time,
    title: e.title,
    kind: e.kind,
    source: e.generator ? 'template' : 'ready',
    generator: e.generator ?? 'evento',
    params: e.params ?? {},
    media: e.media ?? [],
    caption: e.caption ?? '',
    manual: Boolean(e.manual),
    sticker: e.sticker ?? '',
    note: e.note ?? '',
    off: e.off,
    group: e.group ?? '',
  };
}

type Run = (fn: () => Promise<unknown>, ok?: string) => Promise<boolean>;

// --- programmazione -----------------------------------------------------------------------------

function Programme({
  data,
  days,
  setDays,
  canEdit,
  onAdd,
  onEdit,
  run,
  api,
}: {
  data: Data;
  days: number;
  setDays: (n: number) => void;
  canEdit: boolean;
  onAdd: () => void;
  onEdit: (e: Entry) => void;
  run: Run;
  api: Api;
}) {
  const byDay = useMemo(() => {
    const m = new Map<string, Entry[]>();
    for (const e of data.programme) m.set(e.date, [...(m.get(e.date) ?? []), e]);
    return [...m.entries()];
  }, [data.programme]);
  const [hideConditional, setHideConditional] = useState(false);
  const now = nowRome();
  return (
    <Card
      title="Programmazione"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-muted">
            <input
              type="checkbox"
              checked={hideConditional}
              onChange={(e) => setHideConditional(e.target.checked)}
            />
            nascondi quelle «solo se»
          </label>
          <select
            className="h-9 border border-fg/25 bg-page px-2 text-sm"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            aria-label="Giorni da mostrare"
          >
            {[7, 14, 21, 30, 60].map((n) => (
              <option key={n} value={n}>
                {n} giorni
              </option>
            ))}
          </select>
          {canEdit && (
            <button type="button" className={btnPrimary} onClick={onAdd}>
              <Plus className="size-4" aria-hidden /> Aggiungi contenuto
            </button>
          )}
        </div>
      }
    >
      {byDay.length === 0 && (
        <p className="text-sm text-muted">Niente in programma in questi giorni.</p>
      )}
      {byDay.map(([day, entries]) => {
        const shown = entries.filter(
          (e) => !hideConditional || e.posts.length > 0 || !e.when?.startsWith('solo'),
        );
        return (
          <section key={day}>
            <h3 className="sticky top-0 z-[1] border-b border-surface-strong bg-page py-1 text-sm font-medium">
              {dayLabel(day)}
              {day === data.today && <span className="ml-2 text-muted">oggi</span>}
            </h3>
            <ul className="divide-y divide-surface-strong">
              {shown.map((e) => (
                <EntryRow
                  key={e.key}
                  e={e}
                  gen={e.generator ? data.generators[e.generator] : undefined}
                  past={`${e.date}T${e.time}` < now}
                  canEdit={canEdit}
                  onEdit={() => onEdit(e)}
                  run={run}
                  api={api}
                />
              ))}
            </ul>
          </section>
        );
      })}
    </Card>
  );
}

function EntryRow({
  e,
  gen,
  past,
  canEdit,
  onEdit,
  run,
  api,
}: {
  e: Entry;
  gen: Gen | undefined;
  past: boolean;
  canEdit: boolean;
  onEdit: () => void;
  run: Run;
  api: Api;
}) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(e.note ?? '');
  const Icon = KINDS[e.kind].icon;
  const thumbs = e.posts[0]?.images.length
    ? e.posts[0].images.map((path) => ({ path }))
    : (e.media ?? []);
  const save = (patch: { off?: boolean; note?: string }) =>
    run(() =>
      e.source === 'schedule'
        ? api(`/social/occurrences/${e.scheduleId}/${e.date}`, {
            method: 'PUT',
            body: { off: patch.off ?? e.off, note: patch.note ?? e.note ?? '' },
          })
        : api(`/social/slots/${e.slotId}`, { method: 'PATCH', body: patch }),
    );
  const slotState = e.slotStatus ? SLOT_STATUS[e.slotStatus] : undefined;
  return (
    <li
      className={`flex flex-col gap-2 py-3 md:flex-row md:items-start ${e.off || past ? 'opacity-60' : ''}`}
    >
      <div className="flex w-[92px] shrink-0 items-center gap-2">
        <span className="font-medium tabular-nums">{e.time}</span>
        <Icon className="size-4 text-muted" aria-label={KINDS[e.kind].label} />
      </div>
      <div className="min-w-0 flex-1 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <b className={e.off ? 'line-through' : ''}>{e.title}</b>
          <span className="text-xs text-muted">
            {e.source === 'schedule'
              ? `Schedulazione · ${gen?.label ?? e.generator}`
              : e.group
                ? `Contenuto · ${e.group}`
                : gen
                  ? `Da generare · ${gen.label}`
                  : 'Contenuto pronto'}
          </span>
          {e.off && <span className="bg-surface-strong px-2 py-0.5 text-xs">Spento</span>}
          {e.manual && <span className="bg-accent-soft px-2 py-0.5 text-xs">A mano</span>}
          {e.when && !e.posts.length && <span className="text-xs text-muted">{e.when}</span>}
          {slotState && (
            <span className={`px-2 py-0.5 text-xs ${slotState[1]}`}>{slotState[0]}</span>
          )}
        </div>
        {e.sticker && <p className="mt-1 text-xs">🏷 Sticker: {e.sticker}</p>}
        {e.note && !noteOpen && (
          <p className="mt-1 flex items-start gap-1 text-xs">
            <StickyNote className="mt-0.5 size-3 shrink-0" aria-hidden /> {e.note}
          </p>
        )}
        {noteOpen && (
          <form
            className="mt-2 flex gap-2"
            onSubmit={(ev) => {
              ev.preventDefault();
              void save({ note }).then((ok) => ok && setNoteOpen(false));
            }}
          >
            <input
              className={inputCls}
              value={note}
              maxLength={500}
              onChange={(ev) => setNote(ev.target.value)}
              placeholder="Nota per questa data (arriva anche su Telegram)"
              autoFocus
            />
            <button type="submit" className={btnSecondary}>
              Salva
            </button>
          </form>
        )}
        {e.posts.map((p) => (
          <PostLine key={p.id} p={p} run={run} api={api} canEdit={canEdit} />
        ))}
        {canEdit && (
          <div className="mt-1 flex flex-wrap gap-1">
            <button
              type="button"
              className={btnGhost}
              onClick={() => void save({ off: !e.off })}
              disabled={e.posts.length > 0}
              title={e.posts.length ? 'Già preparato: decidi dal post' : undefined}
            >
              {e.off ? 'Riaccendi' : 'Spegni'}
            </button>
            <button type="button" className={btnGhost} onClick={() => setNoteOpen((v) => !v)}>
              <StickyNote className="size-4" aria-hidden /> Nota
            </button>
            {e.source === 'slot' && !e.posts.length && (
              <>
                <button type="button" className={btnGhost} onClick={onEdit}>
                  <Pencil className="size-4" aria-hidden /> Modifica
                </button>
                <button
                  type="button"
                  className={btnGhost}
                  onClick={() =>
                    window.confirm(`Togliere «${e.title}» dalla programmazione?`) &&
                    void run(
                      () => api(`/social/slots/${e.slotId}`, { method: 'DELETE' }),
                      'Contenuto tolto dalla programmazione.',
                    )
                  }
                >
                  <Trash2 className="size-4" aria-hidden /> Elimina
                </button>
              </>
            )}
          </div>
        )}
      </div>
      {thumbs.length > 0 && (
        <div className="flex max-w-full shrink-0 gap-1 overflow-x-auto md:max-w-[300px]">
          {thumbs.slice(0, 5).map((m) => (
            <Thumb key={m.path} m={m} />
          ))}
          {thumbs.length > 5 && (
            <span className="self-center px-1 text-xs text-muted">+{thumbs.length - 5}</span>
          )}
        </div>
      )}
    </li>
  );
}

function Thumb({ m }: { m: MediaItem }) {
  const { config } = useSession();
  // i file del bucket media hanno il percorso del sito (/media/social/…)
  const src = m.path.startsWith('/') ? `${config.mediaUrl ?? ''}${m.path}` : m.path;
  return (
    <a href={src} target="_blank" rel="noreferrer" className="shrink-0">
      {isVideo(m) ? (
        <span className="flex h-[90px] w-[60px] items-center justify-center border border-surface-strong bg-surface">
          <Clapperboard className="size-5 text-muted" aria-label="Video" />
        </span>
      ) : (
        <img
          src={src}
          alt=""
          loading="lazy"
          className="h-[90px] w-[72px] border border-surface-strong object-cover"
        />
      )}
    </a>
  );
}

function PostLine({ p, run, api, canEdit }: { p: Post; run: Run; api: Api; canEdit: boolean }) {
  const [label, cls] = STATUS[p.status] ?? [p.status, 'bg-surface-strong'];
  const open = p.status === 'review' || p.status === 'approved';
  const later = p.publishAt && Date.parse(p.publishAt) > Date.now();
  return (
    <div className="mt-2 border-l-2 border-surface-strong pl-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`px-2 py-0.5 text-xs ${cls}`}>
          {label}
          {p.status === 'approved' && later && p.publishAt ? ` · alle ${hhmm(p.publishAt)}` : ''}
        </span>
        {p.decidedBy && <span className="text-xs text-muted">{p.decidedBy}</span>}
        {p.results &&
          Object.entries(p.results)
            .filter(([, r]) => r.permalink)
            .map(([n, r]) => (
              <a
                key={n}
                href={r.permalink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs underline"
              >
                {NETWORKS[n.replace(/_\d+$/, '')] ?? n}{' '}
                <ExternalLink className="size-3" aria-hidden />
              </a>
            ))}
      </div>
      {p.errors && p.errors.length > 0 && (
        <p className="mt-1 text-xs text-danger">{p.errors.join(' · ')}</p>
      )}
      {open && canEdit && (
        <div className="mt-1 flex flex-wrap gap-1">
          {p.status === 'review' && later && (
            <button
              type="button"
              className={btnSecondary}
              onClick={() =>
                void run(
                  () => api(`/social/${p.id}/publish`, { method: 'POST', body: {} }),
                  `Confermato: si pubblica alle ${hhmm(p.publishAt!)}.`,
                )
              }
            >
              <CalendarClock className="size-4" aria-hidden /> Conferma
            </button>
          )}
          <button
            type="button"
            className={btnPrimary}
            onClick={() =>
              void run(
                () => api(`/social/${p.id}/publish`, { method: 'POST', body: { now: true } }),
                p.manual
                  ? 'Ti mando i file su Telegram.'
                  : 'Pubblicazione avviata: tra un minuto trovi qui i link (e su Telegram).',
              )
            }
          >
            <Send className="size-4" aria-hidden />{' '}
            {p.manual ? 'Mandami i file' : 'Pubblica adesso'}
          </button>
          <button
            type="button"
            className={btnGhost}
            onClick={() =>
              void run(() => api(`/social/${p.id}/skip`, { method: 'POST' }), 'Saltato.')
            }
          >
            <SkipForward className="size-4" aria-hidden /> Salta
          </button>
        </div>
      )}
    </div>
  );
}

// --- contenuto personalizzato -------------------------------------------------------------------

interface Upload {
  name: string;
  pct: number;
  key?: string;
  error?: string;
}

async function uploadFile(api: Api, file: File, onProgress: (pct: number) => void) {
  const type = file.type || UPLOAD_TYPES[file.name.split('.').pop()?.toLowerCase() ?? ''] || '';
  const up = await api<{ url: string; fields: Record<string, string>; key: string }>(
    '/social/uploads',
    { method: 'POST', body: { contentType: type, size: file.size } },
  );
  const form = new FormData();
  for (const [k, v] of Object.entries(up.fields)) form.append(k, v);
  form.append('file', file);
  await postWithProgress(up.url, form, onProgress);
  return up.key;
}

function SlotEditor({
  api,
  generators,
  initial,
  onCancel,
  onSave,
}: {
  api: Api;
  generators: Record<string, Gen>;
  initial: SlotForm;
  onCancel: () => void;
  onSave: (body: unknown, id?: string) => Promise<void>;
}) {
  const [f, setF] = useState<SlotForm>(initial);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<SlotForm>) => setF((cur) => ({ ...cur, ...patch }));
  const gen = generators[f.generator];
  const uploading = uploads.some((u) => !u.key && !u.error);

  const addFiles = (files: FileList | null) => {
    for (const file of Array.from(files ?? [])) {
      const name = file.name;
      setUploads((cur) => [...cur, { name, pct: 0 }]);
      const update = (patch: Partial<Upload>) =>
        setUploads((cur) => cur.map((u) => (u.name === name ? { ...u, ...patch } : u)));
      uploadFile(api, file, (pct) => update({ pct }))
        .then((key) => update({ key, pct: 100 }))
        .catch((e: unknown) => update({ error: errorText(e) }));
    }
  };

  const submit = async () => {
    setBusy(true);
    const body = {
      at: `${f.date}T${f.time}`,
      title: f.title,
      kind: f.kind,
      source: f.source,
      generator: f.source === 'template' ? f.generator : null,
      params: f.source === 'template' ? f.params : {},
      media: f.source === 'ready' ? f.media : [],
      uploadKeys: f.source === 'ready' ? uploads.flatMap((u) => (u.key ? [u.key] : [])) : [],
      caption: f.caption,
      manual: f.manual,
      sticker: f.sticker || null,
      note: f.note || null,
      off: f.off,
      group: f.group || null,
    };
    await onSave(body, f.id);
    setBusy(false);
  };

  return (
    <Card
      title={f.id ? 'Modifica contenuto' : 'Nuovo contenuto in programmazione'}
      actions={
        <button type="button" className={btnGhost} onClick={onCancel} aria-label="Chiudi">
          <X className="size-4" aria-hidden />
        </button>
      }
    >
      <div className="grid gap-4 md:grid-cols-4">
        <Field label="Giorno">
          <input
            type="date"
            className={inputCls}
            value={f.date}
            onChange={(e) => set({ date: e.target.value })}
          />
        </Field>
        <Field label="Ora">
          <input
            type="time"
            className={inputCls}
            value={f.time}
            onChange={(e) => set({ time: e.target.value })}
          />
        </Field>
        <Field label="Titolo" className="md:col-span-2">
          <input
            className={inputCls}
            value={f.title}
            maxLength={120}
            onChange={(e) => set({ title: e.target.value })}
            placeholder="Come lo vedi nel programma e su Telegram"
          />
        </Field>
        <Field label="Tipo">
          <select
            className={inputCls}
            value={f.kind}
            onChange={(e) => set({ kind: e.target.value as Kind })}
          >
            {Object.entries(KINDS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Contenuto" className="md:col-span-3">
          <div className="flex h-10 items-center gap-4 text-sm">
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={f.source === 'ready'}
                onChange={() => set({ source: 'ready' })}
              />
              File già pronti
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={f.source === 'template'}
                onChange={() => set({ source: 'template' })}
              />
              Da generare all’ora prevista
            </label>
          </div>
        </Field>
      </div>

      {f.source === 'template' ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Cosa generare" hint={gen ? `${gen.what} (${gen.when}).` : undefined}>
            <select
              className={inputCls}
              value={f.generator}
              onChange={(e) => set({ generator: e.target.value, params: {} })}
            >
              {Object.entries(generators).map(([k, g]) => (
                <option key={k} value={k}>
                  {g.label}
                </option>
              ))}
            </select>
          </Field>
          {gen && (
            <ParamsFields
              gen={gen}
              params={f.params}
              onChange={(params) => set({ params })}
              api={api}
            />
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <span className="text-label font-medium uppercase tracking-[0.04em] text-muted">
            File (
            {f.kind === 'reel'
              ? 'un video MP4 e, se vuoi, un’immagine per la copertina'
              : f.kind === 'story'
                ? 'una o più storie, 1080×1920'
                : 'una foto o un carosello fino a 10, 1080×1350'}
            )
          </span>
          <div className="flex flex-wrap gap-2">
            {f.media.map((m) => (
              <div key={m.path} className="relative">
                <Thumb m={m} />
                <button
                  type="button"
                  className="absolute right-0 top-0 bg-page/80 p-0.5"
                  aria-label="Togli"
                  onClick={() => set({ media: f.media.filter((x) => x.path !== m.path) })}
                >
                  <X className="size-3" aria-hidden />
                </button>
              </div>
            ))}
            {uploads.map((u) => (
              <span
                key={u.name}
                className={`flex h-[90px] w-[120px] flex-col justify-center border border-surface-strong p-1 text-xs ${u.error ? 'bg-danger-soft' : ''}`}
              >
                <span className="truncate">{u.name}</span>
                <span className="text-muted">{u.error ?? (u.key ? 'caricato' : `${u.pct}%`)}</span>
              </span>
            ))}
            <label className={`${btnSecondary} h-[90px] cursor-pointer`}>
              <Upload className="size-4" aria-hidden /> Carica
              <input
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime"
                className="sr-only"
                onChange={(e) => addFiles(e.target.files)}
              />
            </label>
          </div>
        </div>
      )}

      <Field
        label="Didascalia"
        hint={
          f.kind === 'story'
            ? 'Le storie non hanno didascalia: resta come promemoria.'
            : 'Per Instagram e Facebook. Lasciala vuota per quelli generati: la scrive il generatore.'
        }
      >
        <textarea
          className={`${inputCls} h-28 py-2`}
          value={f.caption}
          maxLength={2200}
          onChange={(e) => set({ caption: e.target.value })}
        />
      </Field>
      <div className="grid gap-4 md:grid-cols-2">
        <Field
          label="Sticker"
          hint="Sondaggio, domanda, link, conto alla rovescia: si mettono a mano, quindi il contenuto diventa «a mano»."
        >
          <input
            className={inputCls}
            value={f.sticker}
            maxLength={300}
            onChange={(e) =>
              set({ sticker: e.target.value, manual: e.target.value ? true : f.manual })
            }
            placeholder="Es. Sondaggio «Sì» / «No», sotto la scritta"
          />
        </Field>
        <Field label="Nota" hint="Per la redazione: arriva anche su Telegram.">
          <input
            className={inputCls}
            value={f.note}
            maxLength={500}
            onChange={(e) => set({ note: e.target.value })}
          />
        </Field>
        <Field label="Gruppo" hint="Per riconoscerlo nel programma, es. «Lancio del sito».">
          <input
            className={inputCls}
            value={f.group}
            maxLength={60}
            onChange={(e) => set({ group: e.target.value })}
          />
        </Field>
        <div className="flex flex-col justify-end gap-2 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={f.manual}
              onChange={(e) => set({ manual: e.target.checked })}
            />
            Da pubblicare a mano (all’ora prevista i file arrivano su Telegram)
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={f.off}
              onChange={(e) => set({ off: e.target.checked })}
            />
            Spento (resta in programma ma non si prepara)
          </label>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          className={btnPrimary}
          disabled={busy || uploading || !f.title}
          onClick={() => void submit()}
        >
          {(busy || uploading) && <Loader2 className="size-4 animate-spin" aria-hidden />}
          Salva in programmazione
        </button>
        <button type="button" className={btnGhost} onClick={onCancel}>
          Annulla
        </button>
      </div>
    </Card>
  );
}

function ParamsFields({
  gen,
  params,
  onChange,
  api,
}: {
  gen: Gen;
  params: Params;
  onChange: (p: Params) => void;
  api: Api;
}) {
  const set = (patch: Partial<Params>) => onChange({ ...params, ...patch });
  const fields: ReactNode[] = [];
  for (const p of gen.params) {
    if (p === 'variant')
      fields.push(
        <Field key={p} label="Formato">
          <select
            className={inputCls}
            value={params.variant ?? 'auto'}
            onChange={(e) => set({ variant: e.target.value as Params['variant'] })}
          >
            <option value="auto">Un giorno l’elenco, un giorno la mappa</option>
            <option value="lista">Sempre l’elenco</option>
            <option value="mappa">Sempre la mappa</option>
          </select>
        </Field>,
      );
    if (p === 'days')
      fields.push(
        <Field key={p} label="Giorni prima dell’evento">
          <input
            type="number"
            min={1}
            max={14}
            className={inputCls}
            value={params.days ?? (gen.kind === 'story' ? 3 : 2)}
            onChange={(e) => set({ days: Number(e.target.value) })}
          />
        </Field>,
      );
    if (p === 'cards')
      fields.push(
        <Field key={p} label="Schede nel carosello">
          <input
            type="number"
            min={2}
            max={8}
            className={inputCls}
            value={params.cards ?? 6}
            onChange={(e) => set({ cards: Number(e.target.value) })}
          />
        </Field>,
      );
    if (p === 'offset')
      fields.push(
        <Field key={p} label="L’evento di quale giorno">
          <select
            className={inputCls}
            value={params.offset ?? 0}
            onChange={(e) => set({ offset: Number(e.target.value) })}
          >
            {OFFSETS.map((label, i) => (
              <option key={i} value={i}>
                {label}
              </option>
            ))}
          </select>
        </Field>,
      );
    if (p === 'category')
      fields.push(
        <Field key={p} label="Tipo di evento">
          <select
            className={inputCls}
            value={params.category ?? ''}
            onChange={(e) => set({ category: e.target.value })}
          >
            <option value="">Tutti</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {categoryLabel(c)}
              </option>
            ))}
          </select>
        </Field>,
      );
    if (p === 'output')
      fields.push(
        <Field key={p} label="Come">
          <select
            className={inputCls}
            value={params.output ?? 'post'}
            onChange={(e) => set({ output: e.target.value as 'post' | 'story' })}
          >
            <option value="post">Post (con didascalia AI)</option>
            <option value="story">Storia</option>
          </select>
        </Field>,
      );
    if (p === 'featured')
      fields.push(
        <label key={p} className="flex items-center gap-2 self-end text-sm">
          <input
            type="checkbox"
            checked={Boolean(params.featured)}
            onChange={(e) => set({ featured: e.target.checked })}
          />
          Con l’etichetta «In evidenza»
        </label>,
      );
    if (p === 'occasion')
      fields.push(
        <Field key={p} label="Occasione">
          <select
            className={inputCls}
            value={params.occasion ?? ''}
            onChange={(e) => set({ occasion: e.target.value || undefined })}
          >
            <option value="">Scegli…</option>
            {Object.entries(gen.occasions ?? {}).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
        </Field>,
      );
    if (p === 'eventId')
      fields.push(
        <EventPicker
          key={p}
          api={api}
          value={params.eventId}
          onPick={(id) => set({ eventId: id })}
        />,
      );
  }
  return <>{fields}</>;
}

function EventPicker({
  api,
  value,
  onPick,
}: {
  api: Api;
  value?: string;
  onPick: (id: string) => void;
}) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState<AdminEventRow[] | null>(null);
  const [picked, setPicked] = useState<AdminEventRow | null>(null);
  useEffect(() => {
    const t = setTimeout(() => {
      const p = new URLSearchParams({ status: 'published', when: 'upcoming', limit: '20' });
      if (q.trim()) p.set('q', q.trim());
      api<{ items: AdminEventRow[] }>(`/events?${p}`)
        .then((r) => {
          setItems(r.items);
          if (value && !picked) setPicked(r.items.find((e) => e.id === value) ?? null);
        })
        .catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, q]);
  return (
    <div className="md:col-span-2">
      <span className="mb-1 block text-label font-medium uppercase tracking-[0.04em] text-muted">
        Evento
      </span>
      {value && (
        <p className="mb-2 text-sm">
          Scelto: <b>{picked?.title ?? value}</b>
          {picked && (
            <span className="text-muted">
              {' '}
              · {formatRange(picked.startDate, picked.endDate)} · {picked.comune}
            </span>
          )}
        </p>
      )}
      <label className="relative block">
        <Search className="absolute left-2 top-3 size-4 text-muted" aria-hidden />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca tra gli eventi pubblicati in corso e futuri…"
          className={`${inputCls} pl-8`}
        />
      </label>
      <ul className="mt-1 max-h-[220px] divide-y divide-surface-strong overflow-y-auto border border-surface-strong">
        {(items ?? []).map((e) => (
          <li key={e.id}>
            <button
              type="button"
              onClick={() => {
                setPicked(e);
                onPick(e.id);
              }}
              className={`flex w-full flex-wrap items-baseline gap-x-3 px-2 py-1.5 text-left text-sm hover:bg-surface ${e.id === value ? 'bg-accent-soft' : ''}`}
            >
              <span className="font-medium">{e.title}</span>
              <span className="text-muted">
                {formatRange(e.startDate, e.endDate)} · {e.comune}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- schedulazioni ------------------------------------------------------------------------------

function emptySchedule(): Schedule {
  return {
    id: '',
    name: '',
    generator: 'evento-del-giorno',
    params: { offset: 0, output: 'post' },
    days: [1],
    monthDay: null,
    time: '12:00',
    enabled: true,
    from: null,
    until: null,
    note: '',
  };
}

function Schedules({
  data,
  canEdit,
  run,
  api,
}: {
  data: Data;
  canEdit: boolean;
  run: Run;
  api: Api;
}) {
  const [editing, setEditing] = useState<Schedule | null>(null);
  const save = async (s: Schedule) => {
    const { id, ...body } = s;
    const ok = await run(
      () =>
        api(id ? `/social/schedules/${id}` : '/social/schedules', {
          method: id ? 'PUT' : 'POST',
          body,
        }),
      'Schedulazione salvata: la programmazione è aggiornata.',
    );
    if (ok) setEditing(null);
  };
  return (
    <>
      {editing && (
        <ScheduleEditor
          generators={data.generators}
          initial={editing}
          onCancel={() => setEditing(null)}
          onSave={save}
          api={api}
        />
      )}
      <Card
        title={`Schedulazioni (${data.schedules.length})`}
        actions={
          canEdit && (
            <button
              type="button"
              className={btnPrimary}
              onClick={() => setEditing(emptySchedule())}
            >
              <Plus className="size-4" aria-hidden /> Nuova schedulazione
            </button>
          )
        }
      >
        <ul className="divide-y divide-surface-strong">
          {data.schedules.map((s) => {
            const g = data.generators[s.generator];
            return (
              <li
                key={s.id}
                className={`flex flex-col gap-2 py-3 md:flex-row md:items-center ${s.enabled ? '' : 'opacity-60'}`}
              >
                <div className="min-w-0 flex-1 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <b>{s.name}</b>
                    <span className="text-xs text-muted">
                      {g?.label ?? s.generator} ·{' '}
                      {KINDS[s.params.output === 'story' ? 'story' : (g?.kind ?? 'post')].label}
                    </span>
                    {!s.enabled && (
                      <span className="bg-surface-strong px-2 py-0.5 text-xs">Spenta</span>
                    )}
                  </div>
                  <p className="text-muted">
                    {daysText(s)} alle {s.time}
                    {s.from ? ` · dal ${s.from}` : ''}
                    {s.until ? ` · fino al ${s.until}` : ''}
                    {g ? ` · ${g.when}` : ''}
                  </p>
                  {s.note && <p className="text-xs">📝 {s.note}</p>}
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    <button
                      type="button"
                      className={btnGhost}
                      onClick={() =>
                        void run(() =>
                          api(`/social/schedules/${s.id}`, {
                            method: 'PUT',
                            body: { ...s, id: undefined, enabled: !s.enabled },
                          }),
                        )
                      }
                    >
                      {s.enabled ? 'Spegni' : 'Accendi'}
                    </button>
                    <button type="button" className={btnGhost} onClick={() => setEditing(s)}>
                      <Pencil className="size-4" aria-hidden /> Modifica
                    </button>
                    <button
                      type="button"
                      className={btnGhost}
                      onClick={() =>
                        window.confirm(`Eliminare la schedulazione «${s.name}»?`) &&
                        void run(
                          () => api(`/social/schedules/${s.id}`, { method: 'DELETE' }),
                          'Schedulazione eliminata.',
                        )
                      }
                    >
                      <Trash2 className="size-4" aria-hidden /> Elimina
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </Card>
    </>
  );
}

function ScheduleEditor({
  generators,
  initial,
  onCancel,
  onSave,
  api,
}: {
  generators: Record<string, Gen>;
  initial: Schedule;
  onCancel: () => void;
  onSave: (s: Schedule) => Promise<void>;
  api: Api;
}) {
  const [s, setS] = useState<Schedule>(initial);
  const set = (patch: Partial<Schedule>) => setS((cur) => ({ ...cur, ...patch }));
  const gen = generators[s.generator];
  const monthly = Boolean(s.monthDay);
  return (
    <Card
      title={s.id ? `Modifica «${initial.name}»` : 'Nuova schedulazione'}
      actions={
        <button type="button" className={btnGhost} onClick={onCancel} aria-label="Chiudi">
          <X className="size-4" aria-hidden />
        </button>
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Nome">
          <input
            className={inputCls}
            value={s.name}
            maxLength={80}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="Es. L’evento del martedì"
          />
        </Field>
        <Field label="Cosa pubblicare" hint={gen ? `${gen.what} (${gen.when}).` : undefined}>
          <select
            className={inputCls}
            value={s.generator}
            onChange={(e) => set({ generator: e.target.value, params: {} })}
          >
            {Object.entries(generators)
              .filter(([, g]) => !g.customOnly)
              .map(([k, g]) => (
                <option key={k} value={k}>
                  {g.label} ({KINDS[g.kind].label.toLowerCase()})
                </option>
              ))}
          </select>
        </Field>
        {gen && (
          <ParamsFields
            gen={gen}
            params={s.params}
            onChange={(params) => set({ params })}
            api={api}
          />
        )}
        <Field label="Quando">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={!monthly}
                onChange={() => set({ monthDay: null, days: s.days.length ? s.days : [1] })}
              />{' '}
              giorni della settimana
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={monthly}
                onChange={() => set({ monthDay: 1, days: [] })}
              />{' '}
              un giorno al mese
            </label>
          </div>
        </Field>
        {monthly ? (
          <Field label="Giorno del mese">
            <input
              type="number"
              min={1}
              max={28}
              className={inputCls}
              value={s.monthDay ?? 1}
              onChange={(e) => set({ monthDay: Number(e.target.value) })}
            />
            <select
              className={`${inputCls} mt-2`}
              aria-label="Spostalo a un giorno della settimana"
              value={s.days[0] ?? ''}
              onChange={(e) => set({ days: e.target.value === '' ? [] : [Number(e.target.value)] })}
            >
              <option value="">Proprio quel giorno</option>
              {WEEKDAYS.map((label, d) => (
                <option key={d} value={d}>
                  Il primo {label} da quel giorno
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <Field label="Giorni">
            <div className="flex flex-wrap gap-1">
              {WEEK.map((label, d) => (
                <label
                  key={d}
                  className={`flex h-10 cursor-pointer items-center border px-2 text-sm ${s.days.includes(d) ? 'border-fg bg-fg text-page' : 'border-fg/25'}`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={s.days.includes(d)}
                    onChange={(e) =>
                      set({
                        days: e.target.checked
                          ? [...s.days, d].sort()
                          : s.days.filter((x) => x !== d),
                      })
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
          </Field>
        )}
        <Field label="Ora di pubblicazione">
          <input
            type="time"
            className={inputCls}
            value={s.time}
            onChange={(e) => set({ time: e.target.value })}
          />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Dal (facoltativo)">
            <input
              type="date"
              className={inputCls}
              value={s.from ?? ''}
              onChange={(e) => set({ from: e.target.value || null })}
            />
          </Field>
          <Field label="Al (facoltativo)">
            <input
              type="date"
              className={inputCls}
              value={s.until ?? ''}
              onChange={(e) => set({ until: e.target.value || null })}
            />
          </Field>
        </div>
        <Field label="Nota fissa" hint="Arriva su Telegram con ogni anteprima.">
          <input
            className={inputCls}
            value={s.note ?? ''}
            maxLength={500}
            onChange={(e) => set({ note: e.target.value })}
          />
        </Field>
        <label className="flex items-center gap-2 self-end text-sm">
          <input
            type="checkbox"
            checked={s.enabled}
            onChange={(e) => set({ enabled: e.target.checked })}
          />{' '}
          Accesa
        </label>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          className={btnPrimary}
          disabled={!s.name}
          onClick={() => void onSave(s)}
        >
          Salva
        </button>
        <button type="button" className={btnGhost} onClick={onCancel}>
          Annulla
        </button>
      </div>
    </Card>
  );
}

// --- pubblicati e impostazioni ------------------------------------------------------------------

function History({
  posts,
  run,
  api,
  canEdit,
}: {
  posts: Post[];
  run: Run;
  api: Api;
  canEdit: boolean;
}) {
  return (
    <Card title={`Post degli ultimi 30 giorni (${posts.length})`}>
      {posts.length === 0 ? (
        <p className="text-sm text-muted">Ancora nessun post.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-surface-strong">
          {posts.map((p) => (
            <li key={p.id} className="flex flex-col gap-3 py-4 md:flex-row">
              <div className="flex shrink-0 gap-1 overflow-x-auto md:w-[260px]">
                {(p.video
                  ? [{ path: p.video, type: 'video' as const }]
                  : p.images.map((path) => ({ path }))
                )
                  .slice(0, 4)
                  .map((m) => (
                    <Thumb key={m.path} m={m} />
                  ))}
              </div>
              <div className="min-w-0 flex-1 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <b>{p.title ?? p.format}</b>
                  <span className="text-muted">{KINDS[p.kind ?? 'post'].label}</span>
                  <span className="text-muted">
                    {formatStamp(p.publishedAt ?? p.publishAt ?? p.createdAt)}
                  </span>
                </div>
                <p className="mt-1 line-clamp-3 whitespace-pre-line text-muted">{p.caption}</p>
                <PostLine p={p} run={run} api={api} canEdit={canEdit} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Settings({
  config: initial,
  isAdmin,
  run,
  api,
}: {
  config: Config;
  isAdmin: boolean;
  run: Run;
  api: Api;
}) {
  const [config, setConfig] = useState(initial);
  return (
    <Card title="Impostazioni">
      <div className="flex flex-wrap items-end gap-4">
        <Field label="Pubblicazione">
          <select
            className={inputCls}
            value={config.mode}
            disabled={!isAdmin}
            onChange={(e) => setConfig({ ...config, mode: e.target.value as Mode })}
          >
            {Object.entries(MODES).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Schedulazioni attive dal"
          hint="Il giorno in cui va online la nuova piattaforma: prima escono solo i contenuti personalizzati. Vuoto: da subito."
        >
          <input
            type="date"
            className={inputCls}
            value={config.activeFrom ?? ''}
            disabled={!isAdmin}
            onChange={(e) => setConfig({ ...config, activeFrom: e.target.value || null })}
          />
        </Field>
        <Field label="Anteprima su Telegram">
          <select
            className={inputCls}
            value={config.leadMinutes}
            disabled={!isAdmin}
            onChange={(e) => setConfig({ ...config, leadMinutes: Number(e.target.value) })}
          >
            {[30, 60, 90, 120, 180, 240].map((m) => (
              <option key={m} value={m}>
                {m < 60 ? `${m} minuti` : `${m / 60} ${m === 60 ? 'ora' : 'ore'}`} prima
              </option>
            ))}
          </select>
        </Field>
        {config.mode === 'review' && (
          <Field label="Senza risposta">
            <select
              className={inputCls}
              value={config.reviewMinutes}
              disabled={!isAdmin}
              onChange={(e) => setConfig({ ...config, reviewMinutes: Number(e.target.value) })}
            >
              {[0, 30, 60, 120, 240].map((m) => (
                <option key={m} value={m}>
                  {m === 0
                    ? 'non si pubblica: serve la conferma'
                    : `si pubblica da solo dopo ${m < 60 ? `${m} minuti` : `${m / 60} ${m === 60 ? 'ora' : 'ore'}`}`}
                </option>
              ))}
            </select>
          </Field>
        )}
        {isAdmin && (
          <button
            type="button"
            className={btnPrimary}
            onClick={() =>
              void run(
                () => api('/social/config', { method: 'PUT', body: config }),
                'Impostazioni salvate: valgono dal prossimo giro (ogni 15 minuti).',
              )
            }
          >
            Salva
          </button>
        )}
      </div>
    </Card>
  );
}
